<script setup lang="ts">
import { ref, reactive, computed } from 'vue'

import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import DeleteConfirmDialog from '@uiKit/components/DeleteConfirmDialog.vue'
import { useAppToast } from '@shared/composables/useAppToast'

import AdminsTable from '../components/AdminsTable.vue'
import AdminFormDrawer from '../components/AdminFormDrawer.vue'
import AdminDetailDrawer from '../components/AdminDetailDrawer.vue'

import { useAdminsListQuery } from '../api/queries/useAdminsListQuery'
import { useCreateAdminMutation } from '../api/mutations/useCreateAdminMutation'
import { useUpdateAdminMutation } from '../api/mutations/useUpdateAdminMutation'
import { useDeleteAdminMutation } from '../api/mutations/useDeleteAdminMutation'
import { useSuspendAdminMutation } from '../api/mutations/useSuspendAdminMutation'
import { useResetMfaMutation } from '../api/mutations/useResetMfaMutation'
import { useResendInviteMutation } from '../api/mutations/useResendInviteMutation'

import type { Admin } from '../types/adminTypes'
import type { UpsertAdminPayload } from '../types/adminTypes'

// Toast is for confirmation of successful actions (per `ux-principles.md`
// §3 — "Toasts are for confirmations of user-initiated actions"). Form
// write errors flow inline through `formApiError`; per-row mutations
// surface failure via toast since they have no inline anchor.
const { addToast } = useAppToast()
const toastSuccess = (title: string, message: string) =>
  addToast({ type: 'success', title, message })
const toastError = (title: string, message: string) => addToast({ type: 'error', title, message })

// ── Listing state ───────────────────────────────────────────────────────

const filters = reactive({ page: 1, limit: 25 })
const { data: adminsPage, isLoading } = useAdminsListQuery(filters)

const rows = computed(() => adminsPage.value?.data ?? [])
const pagination = computed(
  () => adminsPage.value?.pagination ?? { page: 1, limit: 25, totalItems: 0, totalPages: 0 },
)

// ── Mutations ───────────────────────────────────────────────────────────

const createMutation = useCreateAdminMutation()
const updateMutation = useUpdateAdminMutation()
const deleteMutation = useDeleteAdminMutation()
const suspendMutation = useSuspendAdminMutation()
const resetMfaMutation = useResetMfaMutation()
const resendInviteMutation = useResendInviteMutation()

const isFormPending = computed(
  () => createMutation.isPending.value || updateMutation.isPending.value,
)
const isBusy = computed(
  () =>
    isFormPending.value ||
    deleteMutation.isPending.value ||
    suspendMutation.isPending.value ||
    resetMfaMutation.isPending.value ||
    resendInviteMutation.isPending.value,
)

const formApiError = computed(() => {
  const err =
    (formMode.value === 'add' ? createMutation.error.value : updateMutation.error.value) ?? null
  return err ? ((err as { message?: string }).message ?? 'Could not save. Try again.') : null
})

// ── UI state ────────────────────────────────────────────────────────────

const showFormDrawer = ref(false)
const formMode = ref<'add' | 'edit'>('add')
const showDetailDrawer = ref(false)
const selectedAdmin = ref<Admin | null>(null)

// ── Confirm dialog ──────────────────────────────────────────────────────
// Every key per-row action funnels through one confirm dialog driven by
// `pendingAction`. The copy lookup states the consequence (per
// `ux-principles.md` §2 — "Confirm copy states the consequence, not 'Are
// you sure?'"). The handler runs on confirm; the dialog closes on either
// path. Removal already had its own DeleteConfirmDialog and stays
// separate so its danger-tinted icon + copy stay distinct.

type ActionType = 'suspend' | 'resetMfa' | 'resendInvite'

interface PendingAction {
  type: ActionType
  admin: Admin
}

interface ActionCopy {
  title: string
  message: (admin: Admin) => string
  confirmLabel: string
  severity: 'danger' | 'warn' | 'info'
  consequences: string[]
  isPending: () => boolean
  run: (admin: Admin) => Promise<void>
}

const ACTION_COPY: Record<ActionType, ActionCopy> = {
  suspend: {
    title: 'Suspend administrator',
    message: (a) =>
      `${a.name} will lose access to the admin portal until the suspension is lifted.`,
    confirmLabel: 'Suspend',
    severity: 'danger',
    consequences: [
      'Terminate any active sessions immediately',
      'Block sign-in attempts while suspended',
      'Keep the account record and audit history intact',
    ],
    isPending: () => suspendMutation.isPending.value,
    run: async (a) => {
      await suspendMutation.mutateAsync(a.id)
      toastSuccess('Admin suspended', a.name)
    },
  },
  resetMfa: {
    title: 'Reset multi-factor authentication',
    message: (a) =>
      `${a.name}'s authenticator will be revoked and they will be required to re-enrol on next sign-in.`,
    confirmLabel: 'Reset MFA',
    severity: 'warn',
    consequences: [
      'Revoke the current MFA device',
      'Force re-enrolment on next sign-in',
      'Sessions stay active until the operator signs out',
    ],
    isPending: () => resetMfaMutation.isPending.value,
    run: async (a) => {
      await resetMfaMutation.mutateAsync(a.id)
      toastSuccess('MFA reset', `${a.name} must re-enrol on next login.`)
    },
  },
  resendInvite: {
    title: 'Resend sign-in invitation',
    message: (a) =>
      `A new invite email will be sent to ${a.email}. Any previously issued link is invalidated.`,
    confirmLabel: 'Resend invite',
    severity: 'info',
    consequences: [
      'Send a fresh sign-in link to the operator',
      'Invalidate any previously issued invite link',
      'Reset the invitation expiry window',
    ],
    isPending: () => resendInviteMutation.isPending.value,
    run: async (a) => {
      await resendInviteMutation.mutateAsync(a.id)
      toastSuccess('Invite resent', a.email)
    },
  },
}

const pendingAction = ref<PendingAction | null>(null)
const pendingCopy = computed(() =>
  pendingAction.value ? ACTION_COPY[pendingAction.value.type] : null,
)
const showActionConfirm = computed(() => pendingAction.value !== null)

// Identity blocks shown inside each confirmation dialog. The same shape
// surfaces on every modal so the operator confirms they're touching the
// right operator before committing — reduces wrong-record misclicks.
const buildIdentity = (admin: Admin) => ({
  name: admin.name,
  email: admin.email,
  role: admin.role,
})

const actionIdentity = computed(() =>
  pendingAction.value ? buildIdentity(pendingAction.value.admin) : null,
)
const actionConsequences = computed(() => (pendingCopy.value ? pendingCopy.value.consequences : []))

const deleteIdentity = computed(() =>
  selectedAdmin.value ? buildIdentity(selectedAdmin.value) : null,
)

function requestAction(type: ActionType, admin: Admin) {
  pendingAction.value = { type, admin }
}

const handleConfirmAction = async () => {
  if (!pendingAction.value) return
  const { admin, type } = pendingAction.value
  try {
    await ACTION_COPY[type].run(admin)
    pendingAction.value = null
  } catch (e) {
    pendingAction.value = null
    toastError(`${ACTION_COPY[type].title} failed`, (e as Error)?.message ?? 'Try again.')
  }
}

const handleCancelAction = () => {
  pendingAction.value = null
}

// ── Remove (uses its own DeleteConfirmDialog) ──────────────────────────

const showDeleteDialog = ref(false)

const openDelete = (admin: Admin) => {
  selectedAdmin.value = admin
  showDeleteDialog.value = true
}

const handleConfirmDelete = async () => {
  if (!selectedAdmin.value) return
  try {
    await deleteMutation.mutateAsync(selectedAdmin.value.id)
    toastSuccess('Admin removed', selectedAdmin.value.name)
    showDeleteDialog.value = false
    showDetailDrawer.value = false
    selectedAdmin.value = null
  } catch (e) {
    toastError('Remove failed', (e as Error)?.message ?? 'Try again.')
  }
}

// ── Open handlers ──────────────────────────────────────────────────────

const openAdd = () => {
  selectedAdmin.value = null
  formMode.value = 'add'
  createMutation.reset()
  showFormDrawer.value = true
}

const openDetail = (admin: Admin) => {
  selectedAdmin.value = admin
  showDetailDrawer.value = true
}

const openEdit = (admin: Admin) => {
  selectedAdmin.value = admin
  formMode.value = 'edit'
  updateMutation.reset()
  showFormDrawer.value = true
}

// ── Write handler — form submit ────────────────────────────────────────

const handleSubmit = async (payload: UpsertAdminPayload) => {
  try {
    if (formMode.value === 'edit' && selectedAdmin.value) {
      await updateMutation.mutateAsync({ id: selectedAdmin.value.id, payload })
      toastSuccess('Permissions updated', payload.name)
    } else {
      await createMutation.mutateAsync(payload)
      toastSuccess('Invite sent', payload.email)
    }
    showFormDrawer.value = false
    selectedAdmin.value = null
  } catch {
    // surfaced inline through `formApiError`
  }
}

// ── Pagination ──────────────────────────────────────────────────────────

const handlePageChange = (payload: { page: number; limit: number }) => {
  filters.page = payload.page
  filters.limit = payload.limit
}
</script>

<template>
  <Box flex direction="col" gap="4">
    <Box flex align="center" justify="between" gap="3" class="header">
      <Box flex direction="col" gap="1">
        <Text type="title-lg" weight="bold">Team & access control</Text>
        <Text type="body" color="muted">
          Operators who can sign into the admin portal, their role, MFA posture, and invitation
          state.
        </Text>
      </Box>
      <Button label="Invite admin" icon="user-plus" :disabled="isBusy" @click="openAdd" />
    </Box>

    <AdminsTable
      :admins="rows"
      :is-loading="isLoading"
      :page="pagination.page"
      :limit="pagination.limit"
      :total-items="pagination.totalItems"
      @select="openDetail"
      @edit="openEdit"
      @remove="openDelete"
      @suspend="(admin: Admin) => requestAction('suspend', admin)"
      @reset-mfa="(admin: Admin) => requestAction('resetMfa', admin)"
      @resend-invite="(admin: Admin) => requestAction('resendInvite', admin)"
      @view-audit="openDetail"
      @page-change="handlePageChange"
    />

    <AdminDetailDrawer
      :visible="showDetailDrawer"
      :admin="selectedAdmin"
      @update:visible="showDetailDrawer = $event"
    />

    <AdminFormDrawer
      :visible="showFormDrawer"
      :mode="formMode"
      :admin="selectedAdmin"
      :loading="isFormPending"
      :api-error="formApiError"
      @update:visible="showFormDrawer = $event"
      @submit="handleSubmit"
    />

    <DeleteConfirmDialog
      v-if="pendingAction && pendingCopy"
      :visible="showActionConfirm"
      :title="pendingCopy.title"
      :message="pendingCopy.message(pendingAction.admin)"
      :identity="actionIdentity"
      :consequences="actionConsequences"
      :confirm-label="pendingCopy.confirmLabel"
      :severity="pendingCopy.severity"
      :loading="pendingCopy.isPending()"
      @update:visible="(v: boolean) => !v && handleCancelAction()"
      @confirm="handleConfirmAction"
      @cancel="handleCancelAction"
    />

    <DeleteConfirmDialog
      :visible="showDeleteDialog"
      title="Revoke administrator access"
      :message="selectedAdmin ? `Revoke ${selectedAdmin.name}'s access to the admin portal.` : ''"
      :identity="deleteIdentity"
      :consequences="[
        'Immediately terminate any active sessions',
        'Remove admin privileges and assigned roles',
        'Revoke pending invites and MFA enrolment',
      ]"
      :irreversible="true"
      confirm-label="Revoke access"
      :loading="deleteMutation.isPending.value"
      @update:visible="showDeleteDialog = $event"
      @confirm="handleConfirmDelete"
      @cancel="showDeleteDialog = false"
    />
  </Box>
</template>

<style scoped>
.header {
  flex-wrap: wrap;
}
</style>
